import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { CircleDollarSign, CircleX, Download, Flag, Pencil, Plus, Receipt, SearchX, Upload } from 'lucide-react';
import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CardIndicador } from '@/componentes/CardIndicador';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Medida } from '@/componentes/Medida';
import { SemPermissao } from '@/componentes/SemPermissao';
import { RodapeTabela } from '@/componentes/Tabela';
import { formatarCompetencia, formatarDataHora, formatarInteiro, formatarReais } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { perfilAPedir } from '@/lib/perfil-da-licenca';

import { apiDasIntegracoes, baixarArquivoDaSaida, useLote } from './api';
import {
  FILTROS_DA_ENTRADA,
  FILTROS_DA_SAIDA,
  FILTROS_DA_SAIDA_DE_REGISTRO,
  ROTULO_DA_ACAO,
  ROTULO_DA_ENTIDADE,
  ROTULO_DO_FORMATO,
  ROTULO_DO_GRUPO,
  SITUACAO_DO_LOTE,
  oQueALinhaDiz,
  retornoDaPromocao,
  tituloDaLinha,
  type Entidade,
  type FiltroDaLinha,
  type LinhaDoLote,
  type RespostaDaPromocao,
} from './integracoes';
import { JanelaDeDescarte, JanelaDoRetorno } from './JanelasDasIntegracoes';
import { ErroDaIntegracao } from './PaginaIntegracoes';
import { usePermissoesDaIntegracao } from './permissoes';
import { EtiquetaDaIntegracao, SeloDaIntegracao } from './Selo';

/**
 * O LOTE EM PÁGINA PRÓPRIA (módulo 04; 0104): cada linha do arquivo como veio,
 * o que o sistema entendeu dela, a ação (criar, atualizar, vincular, "sem
 * mudança") e o erro com o que fazer — a conferência antes de valer (p.43): o
 * botão diz quanto entra. Na saída, as linhas com a chave de troca e a revisão,
 * os totais por grupo e o retorno do sistema de fora.
 *
 * As linhas vêm do servidor de 100 em 100: o arquivo pode ter 20.000.
 */
const rota = getRouteApi('/cadastros/integracoes/lotes/$loteId');

function ListaDasLinhas({ linhas, direcao, entidade }: { readonly linhas: readonly LinhaDoLote[]; readonly direcao: 'entrada' | 'saida'; readonly entidade: Entidade }) {
  return (
    <ul aria-label="Linhas do lote" className="cartao flex flex-col px-6 py-2">
      {linhas.map((l) => (
        <li key={l.numeroLinha} className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-baseline gap-x-4 gap-y-1 border-t border-borda-sutil py-3 first:border-t-0">
          <span className="txt-apoio whitespace-nowrap tabular-nums text-tinta-secundaria">{`Linha ${formatarInteiro(l.numeroLinha)}`}</span>
          <span className="flex min-w-0 flex-col gap-1">
            <span className="txt-corpo text-tinta">{l.erro ? 'Linha com erro' : tituloDaLinha(l, direcao, entidade)}</span>
            <span className={`txt-apoio text-pretty ${l.erro ? 'text-erro-texto' : 'text-tinta-secundaria'}`}>{oQueALinhaDiz(l, entidade)}</span>
            <span className="mono txt-apoio wrap-anywhere text-tinta-secundaria">{l.bruto}</span>
          </span>
          <span className="flex flex-col items-end gap-1">
            {l.erro ? (
              <SeloDaIntegracao rotulo="Com erro" tom="erro" />
            ) : (
              <SeloDaIntegracao rotulo={ROTULO_DA_ACAO[l.acao ?? ''] ?? l.acao ?? ''} tom={l.acao === 'sem_mudanca' ? 'neutro' : l.acao === 'estorno' ? 'atencao' : 'informacao'} />
            )}
            {direcao === 'saida' && l.valor !== null ? <Medida valor={formatarReais(l.valor)} unidade={`rev. ${String(l.revisao ?? 1)}`} /> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function PaginaLoteDeIntegracao() {
  const { loteId } = rota.useParams();
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();
  const filtro: FiltroDaLinha = busca.filtro ?? 'todas';
  const pagina = busca.pagina ?? 1;
  const detalhe = useLote(loteId, filtro, pagina);
  const pode = usePermissoesDaIntegracao();
  const cliente = useQueryClient();
  const [descartando, setDescartando] = useState(false);
  const [recebendo, setRecebendo] = useState(false);
  const [baixando, setBaixando] = useState<string | null>(null);
  const d = detalhe.data;
  const lote = d?.lote;
  const promover = useMutation({
    mutationFn: (id: string) => apiDasIntegracoes.promover(id),
    meta: { retorno: retornoDe<RespostaDaPromocao>((r) => ({ titulo: 'Lote promovido', ...retornoDaPromocao(r, lote?.nomeArquivo ?? '') })), silenciarFalha: true },
    onSuccess: () => { void cliente.invalidateQueries({ queryKey: ['integracoes'] }); },
  });
  const ir = (f: FiltroDaLinha, p: number) => {
    void navegar({ search: { ...(f === 'todas' ? {} : { filtro: f }), ...(p > 1 ? { pagina: p } : {}) } });
  };

  if (detalhe.isError && !d) {
    return (
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina sobretitulo="Integrações" titulo="Lote" acoes={<Link to="/cadastros/integracoes/lotes" className="botao botao-secundario">Voltar aos lotes</Link>} />
          <ErroDaIntegracao oQue="o lote" erro={detalhe.error} aoTentar={() => { void detalhe.refetch(); }} />
        </div>
      </div>
    );
  }

  const entrada = lote?.direcao === 'entrada';
  /* 0108: a saida de registro (pedido e cadastro) tem novo, alterado e cancelado. */
  const deRegistro = !entrada && lote !== undefined && lote.entidade !== 'lancamento_gerencial';
  const filtros = entrada ? FILTROS_DA_ENTRADA : deRegistro ? FILTROS_DA_SAIDA_DE_REGISTRO : FILTROS_DA_SAIDA;
  const resumoDoRegistro = (lote?.totais['resumo'] ?? null) as { novos?: number; alterados?: number; cancelados?: number } | null;
  const emConferencia = lote?.situacao === 'estagio';
  const validas = lote?.linhasValidas ?? 0;
  const porGrupo = (lote?.totais['porGrupo'] ?? null) as Record<string, number> | null;
  const porConta = (lote?.totais['porContaContabil'] ?? []) as { competencia: string; contaContabil: string | null; valor: number }[];
  const semConta = Number(lote?.totais['semContaContabil'] ?? 0);
  const total = d?.totalDoFiltro ?? 0;
  const porPagina = d?.porPagina ?? 100;

  return (
    <>
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo={lote ? `Integrações · ${entrada ? 'entrada' : 'saída'} de ${ROTULO_DA_ENTIDADE[lote.entidade].toLowerCase()}` : 'Integrações'}
            titulo={lote?.nomeArquivo ?? 'Lote'}
            subtitulo={lote ? `${entrada ? `De ${lote.sistema}` : `Para ${lote.sistema}`} · ${ROTULO_DO_FORMATO[lote.formato]} · ${formatarDataHora(new Date(lote.criadoEm))} por ${lote.criadoPor ?? '—'}` : ''}
            acoes={
              <>
                <Link to="/cadastros/integracoes/lotes" className="botao botao-secundario">Voltar aos lotes</Link>
                {lote && entrada && emConferencia && pode.importar ? (
                  <>
                    <Botao nivel="secundario" onClick={() => { setDescartando(true); }}>Descartar lote</Botao>
                    <Botao nivel="primario" disabled={promover.isPending || validas === 0} onClick={() => { promover.mutate(lote.id); }}>
                      {promover.isPending ? 'Promovendo…' : `Promover ${formatarInteiro(validas)} ${validas === 1 ? 'linha' : 'linhas'}`}
                    </Botao>
                  </>
                ) : null}
                {lote && !entrada && pode.exportar ? (
                  <Botao
                    nivel="secundario"
                    disabled={baixando !== null}
                    onClick={() => {
                      setBaixando('');
                      baixarArquivoDaSaida(lote.id).then(() => { setBaixando(null); }, (e: unknown) => { setBaixando(explicarFalha(e).comoResolver); });
                    }}
                  >
                    <Download aria-hidden="true" className="size-[var(--icone-controle)]" />
                    Baixar arquivo
                  </Botao>
                ) : null}
                {lote && !entrada && lote.situacao === 'gerado' && pode.exportar ? (
                  <Botao nivel="fantasma" onClick={() => { setDescartando(true); }}>Descartar saída</Botao>
                ) : null}
                {lote && !entrada && (lote.situacao === 'gerado' || lote.situacao === 'divergente') && pode.conciliar ? (
                  <Botao nivel="primario" onClick={() => { setRecebendo(true); }}>
                    <Upload aria-hidden="true" className="size-[var(--icone-controle)]" />
                    Receber retorno
                  </Botao>
                ) : null}
              </>
            }
          />
          {promover.isError ? (
            <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
              <span className="txt-corpo text-tinta">{explicarFalha(promover.error).erro}</span>
              <span className="txt-apoio text-tinta-secundaria">{explicarFalha(promover.error).comoResolver}</span>
            </div>
          ) : null}
          {baixando ? <p role="alert" className="txt-apoio text-erro-texto">{baixando}</p> : null}

          <FaixaIndicadores>
            <CardIndicador rotulo="Linhas" valor={lote ? formatarInteiro(lote.linhas) : ''} contexto={lote ? `${formatarInteiro(validas)} válidas` : 'no arquivo'} icone={entrada ? Upload : Download} tom="marca" carregando={!lote} />
            {entrada ? (
              <>
                <CardIndicador rotulo="A criar" valor={d ? formatarInteiro(d.contagem['criar'] ?? 0) : ''} contexto={d ? `${formatarInteiro(d.contagem['vincular'] ?? 0)} a vincular ao que já existe` : 'cadastros novos'} icone={Plus} tom="disponivel" carregando={!d} />
                <CardIndicador rotulo="A atualizar" valor={d ? formatarInteiro(d.contagem['atualizar'] ?? 0) : ''} contexto={d ? `${formatarInteiro(d.contagem['sem_mudanca'] ?? 0)} sem mudança` : 'com a correspondência'} icone={Pencil} tom="disponivel" carregando={!d} />
                <CardIndicador rotulo="Com erro" valor={d ? formatarInteiro(d.contagem['erro'] ?? 0) : ''} contexto="ficam de fora, com o que fazer" icone={CircleX} tom="parada" alerta={(d?.contagem['erro'] ?? 0) > 0} carregando={!d} />
              </>
            ) : deRegistro ? (
              <>
                <CardIndicador
                  rotulo="Registros"
                  valor={lote ? formatarInteiro(Number(lote.totais['registros'] ?? 0)) : ''}
                  contexto={resumoDoRegistro ? `${formatarInteiro(resumoDoRegistro.novos ?? 0)} novos, ${formatarInteiro(resumoDoRegistro.alterados ?? 0)} alterados, ${formatarInteiro(resumoDoRegistro.cancelados ?? 0)} cancelados` : ''}
                  icone={Plus}
                  tom="disponivel"
                  carregando={!lote}
                />
                <CardIndicador
                  rotulo={lote?.entidade === 'pedido_de_compra' ? 'Valor dos pedidos' : 'Revisão do lote'}
                  valor={lote ? (lote.entidade === 'pedido_de_compra' ? formatarReais(Number(lote.totais['valor'] ?? 0)) : formatarInteiro(lote.revisao ?? 1)) : ''}
                  contexto={lote?.competencia ? `saída de ${formatarCompetencia(lote.competencia)}` : ''}
                  icone={Pencil}
                  tom="neutro"
                  carregando={!lote}
                />
                <CardIndicador rotulo="Divergências" valor={lote ? formatarInteiro(lote.divergenciasAbertas) : ''} contexto="abertas no retorno" icone={Flag} tom="standby" alerta={(lote?.divergenciasAbertas ?? 0) > 0} carregando={!lote} />
              </>
            ) : (
              <>
                <CardIndicador rotulo="Receita" valor={porGrupo ? formatarReais(porGrupo['receita'] ?? 0) : ''} contexto={lote?.competencia ? `competência ${formatarCompetencia(lote.competencia)}, revisão ${String(lote.revisao ?? 1)}` : ''} icone={Receipt} tom="operando" carregando={!lote} />
                <CardIndicador
                  rotulo="Custos"
                  valor={porGrupo ? formatarReais((porGrupo['custo_direto'] ?? 0) + (porGrupo['custo_indireto'] ?? 0) + (porGrupo['depreciacao'] ?? 0)) : ''}
                  contexto="diretos, indiretos e depreciação"
                  icone={CircleDollarSign}
                  tom="neutro"
                  carregando={!lote}
                />
                <CardIndicador rotulo="Divergências" valor={lote ? formatarInteiro(lote.divergenciasAbertas) : ''} contexto="abertas no retorno" icone={Flag} tom="standby" alerta={(lote?.divergenciasAbertas ?? 0) > 0} carregando={!lote} />
              </>
            )}
          </FaixaIndicadores>

          {lote ? (
            <section className="cartao flex flex-col gap-3 p-6">
              <div className="flex flex-wrap items-center gap-2">
                <SeloDaIntegracao {...SITUACAO_DO_LOTE[lote.situacao]} />
                {lote.demonstracao ? <EtiquetaDaIntegracao>Sistema de demonstração</EtiquetaDaIntegracao> : null}
              </div>
              <p className="txt-apoio text-pretty text-tinta-secundaria">
                {entrada
                  ? emConferencia
                    ? 'Nada vale antes de você promover. Promover leva só as linhas válidas, decide de novo cada uma pela correspondência de agora, e grava o cadastro e a correspondência juntos.'
                    : lote.situacao === 'promovido'
                      ? `Promovido em ${lote.decididoEm ? formatarDataHora(new Date(lote.decididoEm)) : '—'} por ${lote.decididoPor ?? '—'}.`
                      : `Descartado: ${lote.motivoDescarte ?? ''}`
                  : deRegistro
                    ? 'Sai o que mudou desde a última saída: cada registro com a chave de troca (o id dele na plataforma) e a revisão; o pedido cancelado sai como cancelamento. O retorno traz a chave do sistema de fora, que vira a correspondência.'
                    : `${lote.motivo ? `Motivo da nova saída: ${lote.motivo}. ` : ''}Cada linha traz a chave de troca e a revisão; o que deixou de existir sai como estorno. O sistema de fora aplica a revisão maior.`}
              </p>
              {!entrada && semConta > 0 ? (
                <p className="txt-apoio text-pretty text-atencao-texto">{`${formatarInteiro(semConta)} linhas sem conta contábil: complete o de-para no plano de contas (Financeiro › Plano de contas) e gere o mês de novo.`}</p>
              ) : null}
              {!entrada && porConta.length > 0 ? (
                <table className="w-full">
                  <caption className="txt-rotulo-campo pb-2 text-left">Totais por conta contábil</caption>
                  <thead>
                    <tr>
                      <th scope="col" className="txt-apoio text-left text-tinta-secundaria">Conta contábil</th>
                      <th scope="col" className="txt-apoio text-right text-tinta-secundaria">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porConta.map((c) => (
                      <tr key={c.contaContabil ?? '-'} className="border-t border-borda-sutil">
                        <td className="codigo py-2 text-tinta">{c.contaContabil ?? 'Sem conta contábil'}</td>
                        <td className="txt-corpo py-2 text-right tabular-nums text-tinta">{formatarReais(c.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
              {!entrada && porGrupo ? (
                <p className="txt-apoio text-tinta-secundaria">
                  {['receita', 'custo_direto', 'custo_indireto', 'depreciacao'].map((g) => `${ROTULO_DO_GRUPO[g] ?? g} ${formatarReais(porGrupo[g] ?? 0)}`).join(' · ')}
                </p>
              ) : null}
              {(d?.retornos ?? []).length > 0 ? (
                <ul className="flex flex-col" aria-label="Retornos recebidos">
                  {(d?.retornos ?? []).map((r) => (
                    <li key={r.id} className="txt-apoio border-t border-borda-sutil py-2 text-tinta-secundaria">
                      {`Retorno ${r.nomeArquivo} · ${formatarDataHora(new Date(r.recebidoEm))} por ${r.recebidoPor ?? '—'} · ${formatarInteiro(Number(r.resumo['aceitas'] ?? 0))} aceitas, ${formatarInteiro(Number(r.resumo['divergencias'] ?? 0))} divergências`}
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ) : null}

          {d?.semLicenca ? (
            <SemPermissao
              titulo={d.semLicenca === 'compras.ler' ? 'As linhas da saída são os pedidos de compra' : 'As linhas da saída são os números da DRE'}
              modulo={d.semLicenca === 'compras.ler' ? 'as compras' : 'o financeiro'}
              perfil={perfilAPedir(d.semLicenca)}
            />
          ) : (
            <>
              <div role="group" aria-label="Filtro das linhas" className="flex flex-wrap gap-2">
                {filtros.map((f) => {
                  const n = f === 'todas' ? (lote?.linhas ?? 0) : (d?.contagem[f] ?? 0);
                  return (
                    <button
                      key={f}
                      type="button"
                      aria-pressed={filtro === f}
                      className={`chip ${filtro === f ? 'chip-aplicado' : ''}`}
                      onClick={() => { ir(f, 1); }}
                    >
                      {`${f === 'todas' ? 'Todas' : ROTULO_DA_ACAO[f] ?? f} · ${formatarInteiro(n)}`}
                    </button>
                  );
                })}
              </div>
              {detalhe.isPending ? (
                <Carregando oQue="Carregando as linhas do lote" className="cartao flex flex-col gap-3 p-6">
                  {[0, 1, 2, 3].map((i) => <Barra key={i} className={`h-4 ${i % 2 ? 'w-2/3' : 'w-1/2'}`} />)}
                </Carregando>
              ) : (d?.linhas ?? []).length === 0 ? (
                filtro === 'todas' ? (
                  <EstadoVazio icone={Upload} titulo="O lote não tem linhas" descricao="O arquivo veio só com o cabeçalho. Exporte de novo do sistema de fora com os registros." />
                ) : (
                  <EstadoVazio
                    icone={SearchX}
                    titulo={`Nenhuma linha em ${(ROTULO_DA_ACAO[filtro] ?? filtro).toLowerCase()}`}
                    descricao="Limpe o filtro para ver todas as linhas."
                    acao={<Botao nivel="secundario" onClick={() => { ir('todas', 1); }}>Limpar filtro</Botao>}
                  />
                )
              ) : (
                <>
                  <ListaDasLinhas linhas={d?.linhas ?? []} direcao={lote?.direcao ?? 'entrada'} entidade={lote?.entidade ?? 'fornecedor'} />
                  <div className="cartao">
                    <RodapeTabela
                      primeiro={total === 0 ? 0 : (pagina - 1) * porPagina + 1}
                      ultimo={Math.min(pagina * porPagina, total)}
                      total={total}
                      pagina={pagina}
                      paginas={Math.max(1, Math.ceil(total / porPagina))}
                      porPagina={porPagina}
                      opcoesPorPagina={[porPagina]}
                      aoMudarPagina={(p) => { ir(filtro, p); }}
                      aoMudarPorPagina={() => undefined}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      {lote && ((entrada && pode.importar) || (!entrada && pode.exportar)) ? (
        <JanelaDeDescarte alvo={descartando ? lote : null} aoFechar={() => { setDescartando(false); }} />
      ) : null}
      {lote && !entrada && pode.conciliar ? <JanelaDoRetorno alvo={recebendo ? lote : null} aoFechar={() => { setRecebendo(false); }} /> : null}
    </>
  );
}
